import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}

/* ------------------------------------------------------------------
 * Puffsole: free shipping protection.
 * Adds the free Shipping Protection line to every cart that has real
 * items. If the shopper removes it, we remember that and stop adding
 * it back until the cart is emptied or they add it back themselves.
 * The header count ignores the protection line so it reads as pairs.
 * ------------------------------------------------------------------ */
(function () {
  const VARIANT_ID = 53919468978490;
  const OPT_OUT = 'ps_protection_optout';
  const SEEN = 'ps_protection_seen';
  const originalFetch = window.fetch.bind(window);
  let internal = false;

  const cartJson = () => originalFetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const isProtection = (line) => line.variant_id === VARIANT_ID;
  const realLines = (cart) => cart.items.filter((line) => !isProtection(line));
  const hasProtection = (cart) => cart.items.some(isProtection);
  const optedOut = (cart) => String((cart.attributes || {})[OPT_OUT] || '') === 'true';
  const visibleCount = (cart) => {
    const line = cart.items.find(isProtection);
    return Math.max(0, cart.item_count - (line ? line.quantity : 0));
  };

  const flag = {
    get: () => {
      try {
        return sessionStorage.getItem(SEEN) === '1';
      } catch (_) {
        return false;
      }
    },
    set: (on) => {
      try {
        if (on) sessionStorage.setItem(SEEN, '1');
        else sessionStorage.removeItem(SEEN);
      } catch (_) {
        // no-op
      }
    },
  };

  const post = (url, payload) => {
    internal = true;
    return originalFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .catch(() => null)
      .then((result) => {
        internal = false;
        return result;
      });
  };

  const syncBadge = () =>
    cartJson()
      .then((cart) => {
        const icon = document.querySelector('cart-icon');
        if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(visibleCount(cart), false);
        return cart;
      })
      .catch(() => null);

  const refreshCartUi = () =>
    syncBadge()
      .then(() => {
        const host = document.getElementById('shopify-section-cart-drawer-section');
        if (!host) return null;

        return originalFetch(`${window.location.pathname}?sections=cart-drawer-section`)
          .then((r) => r.json())
          .then((sections) => {
            const html = sections['cart-drawer-section'];
            if (html) host.innerHTML = html;
          });
      })
      .catch(() => {});

  const reconcile = () =>
    cartJson()
      .then((cart) => {
        const lines = realLines(cart);
        const present = hasProtection(cart);

        if (lines.length === 0) {
          flag.set(false);
          if (present) return post('/cart/change.js', { id: String(VARIANT_ID), quantity: 0 }).then(refreshCartUi);
          if (optedOut(cart)) return post('/cart/update.js', { attributes: { [OPT_OUT]: '' } }).then(syncBadge);
          return syncBadge();
        }

        if (present) {
          flag.set(true);
          return syncBadge();
        }

        if (flag.get()) {
          flag.set(false);
          return post('/cart/update.js', { attributes: { [OPT_OUT]: 'true' } }).then(syncBadge);
        }

        if (optedOut(cart)) return syncBadge();

        return post('/cart/add.js', { items: [{ id: VARIANT_ID, quantity: 1 }] }).then(() => {
          flag.set(true);
          if (window.Theme && window.Theme.template && window.Theme.template.name === 'cart') {
            window.location.reload();
            return null;
          }
          return refreshCartUi();
        });
      })
      .catch(() => {});

  const pathOf = (input) => {
    try {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      return new URL(url, window.location.origin).pathname;
    } catch (_) {
      return '';
    }
  };

  window.fetch = function (input, init) {
    if (internal) return originalFetch(input, init);

    const path = pathOf(input);
    const body = init && typeof init.body === 'string' ? init.body : '';

    // The shopper asked for the protection back: honour it and forget the opt-out.
    if (/\/cart\/add(\.js)?$/.test(path) && body.indexOf(String(VARIANT_ID)) !== -1) {
      return originalFetch(input, init).then((response) => {
        flag.set(true);
        post('/cart/update.js', { attributes: { [OPT_OUT]: '' } }).then(syncBadge);
        return response;
      });
    }

    if (/\/cart\/add(\.js)?$/.test(path) && body) {
      return cartJson()
        .then((cart) => {
          if (hasProtection(cart) || optedOut(cart)) return originalFetch(input, init);

          let payload;
          try {
            payload = JSON.parse(body);
          } catch (_) {
            return originalFetch(input, init);
          }

          const lines = Array.isArray(payload.items)
            ? payload.items.slice()
            : [
                {
                  id: payload.id,
                  quantity: payload.quantity == null ? 1 : payload.quantity,
                  properties: payload.properties,
                  selling_plan: payload.selling_plan,
                },
              ];

          const rest = {};
          const skip = ['id', 'quantity', 'properties', 'selling_plan', 'items'];
          Object.keys(payload).forEach((key) => {
            if (skip.indexOf(key) === -1) rest[key] = payload[key];
          });
          rest.items = lines.concat([{ id: VARIANT_ID, quantity: 1 }]);

          flag.set(true);
          return originalFetch(input, Object.assign({}, init, { body: JSON.stringify(rest) })).then((response) => {
            window.setTimeout(syncBadge, 200);
            return response;
          });
        })
        .catch(() => originalFetch(input, init));
    }

    const response = originalFetch(input, init);

    if (/\/cart\/(add|change|update|clear)(\.js)?$/.test(path)) {
      response.then(() => window.setTimeout(reconcile, 60)).catch(() => {});
    }

    return response;
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, () => window.setTimeout(syncBadge, 250));

  const start = () => {
    syncBadge();
    window.setTimeout(reconcile, 400);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
